"use client";

import { SHELL_RAIL_FLAT, SHELL_RAIL_LEFT, SHELL_RAIL_RIGHT } from "./shell";
import { PAGE_HIGHLIGHT_SURFACE_CLASS } from "./surface";
import { useSkipInitialEnterAnimationStyle } from "./useSkipInitialEnterAnimation";
import { cn } from "@/lib/utils";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { type ReactNode } from "react";

const RAIL_CARD_CHROME =
  "border border-surface-tertiary rounded-lg overflow-hidden";

// Matches Page.Layout's `gap-1` (4px) between flex children. When the inline
// rail is closed, we cancel this gap via negative margin on our side facing
// the previous sibling, so the rail's neighbor isn't 4px short of the layout
// edge. When the rail opens, the margin animates back to 0 in lockstep with
// the width transition, making the gap appear smoothly.
const LAYOUT_GAP_PX = 4;

type RailSide = "left" | "right";
type RailSurface = "primary" | "highlight";

const getRailSurfaceClassName = (surface: RailSurface) =>
  surface === "highlight"
    ? `${PAGE_HIGHLIGHT_SURFACE_CLASS} bg-surface-highlight`
    : "bg-surface-primary";

/**
 * Side-positioned card that opens next to a `Page.Card`. Two variants:
 *
 * - `variant="inline"`: rail is a flex sibling of `Page.Card`, animates
 *   its width between 0 and `width` so it pushes the card content over.
 * - `variant="overlay"`: rail is an absolutely-positioned floating card
 *   inset from the layout edges, slides in from `side`.
 *
 * Both variants render the same card chrome. Callers pick the variant
 * based on their own breakpoint/state. Other presentations (drawers,
 * fullBleed card-bound overlays) are not `Page.Rail`'s job and should be
 * built directly with `Drawer` / `DialogPrimitive` where they live.
 *
 * `resizable` (inline only) renders a drag handle on the rail's inner edge
 * and forwards `onResizeStart` to the caller. The caller owns the drag
 * state machine: track the cursor, update `width`, and set
 * `animate="none"` while dragging so the rail tracks the cursor without
 * lag.
 */
export const PageRail = ({
  side,
  open,
  onOpenChange,
  width,
  variant,
  animate = "width",
  skipInitialEnterAnimation = false,
  resizable = false,
  onResizeStart,
  ariaLabel,
  className,
  surface = "primary",
  children,
}: {
  side: RailSide;
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  width?: string;
  variant: "inline" | "overlay";
  animate?: "width" | "none";
  skipInitialEnterAnimation?: boolean;
  resizable?: boolean;
  onResizeStart?: (e: React.MouseEvent) => void;
  ariaLabel: string;
  className?: string;
  surface?: RailSurface;
  children: ReactNode;
}) => {
  if (variant === "inline") {
    const aside = (
      <InlineRail
        side={side}
        open={open}
        width={width}
        animate={animate}
        ariaLabel={ariaLabel}
        className={className}
        surface={surface}
      >
        {children}
      </InlineRail>
    );
    const handle =
      resizable && open ? <ResizeHandle onMouseDown={onResizeStart} /> : null;
    return side === "right" ? (
      <>
        {handle}
        {aside}
      </>
    ) : (
      <>
        {aside}
        {handle}
      </>
    );
  }
  return (
    <OverlayRail
      side={side}
      open={open}
      onOpenChange={onOpenChange}
      width={width}
      skipInitialEnterAnimation={skipInitialEnterAnimation}
      ariaLabel={ariaLabel}
      surface={surface}
    >
      {children}
    </OverlayRail>
  );
};

export const ResizeHandle = ({
  onMouseDown,
}: {
  onMouseDown?: (e: React.MouseEvent) => void;
}) => {
  return (
    <div className="shrink-0 w-0 relative z-10">
      <div
        onMouseDown={onMouseDown}
        className="absolute top-0 -left-[8px] w-[16px] h-full flex items-center justify-center cursor-col-resize group/handle"
      >
        <div className="w-[2px] h-full max-h-[64px] rounded-full bg-transparent transition-colors duration-150 group-hover/handle:bg-border-secondary" />
      </div>
    </div>
  );
};

const InlineRail = ({
  side,
  open,
  width,
  animate,
  ariaLabel,
  className,
  surface,
  children,
}: {
  side: RailSide;
  open: boolean;
  width?: string;
  animate: "width" | "none";
  ariaLabel: string;
  className?: string;
  surface: RailSurface;
  children: ReactNode;
}) => {
  // Cancel the layout's flex gap on the side facing our neighbor while closed.
  // When the rail is at width 0, the layout's `gap-1` would otherwise reserve
  // 4px of dead space between the neighbor and us; this negative margin pulls
  // that space back so the neighbor extends to the layout's padding edge.
  const closedGapCancel = open ? 0 : -LAYOUT_GAP_PX;
  return (
    <aside
      aria-label={ariaLabel}
      aria-hidden={!open}
      inert={!open || undefined}
      data-side={side}
      className={cn(
        "h-full shrink-0 overflow-hidden",
        animate === "width" &&
          "transition-[width,margin-left,margin-right] duration-300 ease-in-out will-change-[width,margin-left,margin-right]",
        className
      )}
      style={{
        width: open ? width : 0,
        marginLeft: side === "right" ? closedGapCancel : 0,
        marginRight: side === "left" ? closedGapCancel : 0,
      }}
    >
      <div
        style={{ width }}
        className={cn(
          "h-full",
          RAIL_CARD_CHROME,
          getRailSurfaceClassName(surface),
          // Inside the shell the inline rail's card chrome flattens into a
          // divider on the side facing the content. The overlay stays a card.
          SHELL_RAIL_FLAT,
          side === "left" ? SHELL_RAIL_LEFT : SHELL_RAIL_RIGHT
        )}
      >
        {children}
      </div>
    </aside>
  );
};

const OverlayRail = ({
  side,
  open,
  onOpenChange,
  width,
  skipInitialEnterAnimation,
  ariaLabel,
  surface,
  children,
}: {
  side: RailSide;
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  width?: string;
  skipInitialEnterAnimation: boolean;
  ariaLabel: string;
  surface: RailSurface;
  children: ReactNode;
}) => {
  const skipInitialStyle = useSkipInitialEnterAnimationStyle();
  const initialStyle = skipInitialEnterAnimation ? skipInitialStyle : undefined;

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onOpenChange?.(false);
      }}
      modal={false}
    >
      <DialogPrimitive.Content
        aria-label={ariaLabel}
        style={{ width, maxWidth: "100%", ...initialStyle }}
        className={cn(
          "absolute top-1 bottom-1 z-30 shadow-xl focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out duration-300",
          RAIL_CARD_CHROME,
          getRailSurfaceClassName(surface),
          side === "right"
            ? "right-1 data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right"
            : "left-1 data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left"
        )}
      >
        <DialogPrimitive.Title className="sr-only">
          {ariaLabel}
        </DialogPrimitive.Title>
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Root>
  );
};

// Re-export for feature-level overlays that need the same flicker-avoidance
// trick when their mount is gated on a breakpoint (e.g. card-bound fullBleed
// overlays implemented outside Page.Rail).
export { useSkipInitialEnterAnimationStyle };
